import { KeyResourcesItemProps } from "components/atoms/KeyResourcesItem";
import { HeroSection } from "components/organisms/HeroSection";
import { FC } from "react";
import { Page } from "../../templates/Page";
import BottomSection from "components/organisms/BottomSection";
import { color } from "@styles";
import { NewProductIntro } from "components/atoms/NewProductIntro";
import { Clouds } from "./clouds";
import { YugiCallOutImg, YugiNewspaper } from "./styles";
import { H2 } from "components/atoms/Heading";
import { TabSwitcherProducts } from "components/templates/TabSwitcherProducts";
import { StandardSection } from "components/templates/StandardSection";
import { NewZigZag } from "components/molecules/NewZigZag";
import { CallOutAd } from "components/molecules/CallOutAd";
import { QuoteCarousel } from "components/organisms/QuoteCarousel";
import { useLottie } from "hooks/useLottie";
import { StaticRoutes } from "@routes";
import { useTranslations } from "next-intl";
import { useLocale } from "@hooks";
import { useLocaleData } from "hooks/useLocaleData";
import data from "./translations";
import { LatestCaseStudy } from "utils/server";
import { P1 } from "components/atoms/Paragraph";
import { Anchor } from "components/atoms/Anchor";
import { ResponsiveZigZagImage } from "components/atoms/ResponsiveZigZagImage";
import { YULIFE_DATA } from "utils/_constants";

interface EmployeeBenefitsProps {
  keyResources: KeyResourcesItemProps[];
  latestCaseStudy: LatestCaseStudy;
}

const EmployeeBenefits: FC<EmployeeBenefitsProps> = ({ keyResources, latestCaseStudy }) => {
  const animationRef = useLottie("/static/lottie/YugiNewspaper.json");
  const t = useTranslations("employee-benefits");
  const { locale } = useLocale();
  const { faq } = useLocaleData(data);

  return (
    <Page
      latestCaseStudy={latestCaseStudy}
      navColor="transparent"
      hero={
        <HeroSection
          eyeBrow="Insurance as an employee benefit"
          title={t<string>("hero.title")}
          subHeading={
            <>
              {t.rich("hero.sub-heading", {
                life: (chunks) => (
                  <Anchor
                    key="life"
                    color={color.P100}
                    href={StaticRoutes.insuranceGroupLifeInsurance}
                    $isUnderlined
                  >
                    {chunks}
                  </Anchor>
                ),
                income: (chunks) => (
                  <Anchor
                    key="income"
                    color={color.P100}
                    href={StaticRoutes.insuranceGroupIncomeProtection}
                    $isUnderlined
                  >
                    {chunks}
                  </Anchor>
                ),
                funeral: (chunks) => (
                  <Anchor
                    key="funeral"
                    color={color.P100}
                    href={StaticRoutes.insuranceFuneralCover}
                    $isUnderlined
                  >
                    {chunks}
                  </Anchor>
                ),
                disability: (chunks) => (
                  <Anchor
                    key="disability"
                    color={color.P100}
                    href={StaticRoutes.insuranceLumpSumDisability}
                    $isUnderlined
                  >
                    {chunks}
                  </Anchor>
                ),
              })}
            </>
          }
          buttonText="Speak to our team"
          image={<YugiNewspaper ref={animationRef} />}
          textPt="2rem"
          backgroundColor={color.S310}
          clouds={<Clouds />}
          statBar={{
            heading: "#1 rated employee benefit",
            tooltipCopy: "Trustpilot.com",
            backgroundColor: color.S3100,
            stats: [
              {
                value: "93%",
                description: "highly engaged users",
                tooltip:
                  "People who have downloaded the app in the last year and have been active in the last 30 days",
              },
              {
                value: "181%",
                description: "return on investment",
                tooltip: t<string>("stats.stat2.tooltip"),
              },
              {
                value: "85%",
                description: "feel more productive",
                tooltip: "YuLife user survey, 2022",
              },
            ],
          }}
        />
      }
      fullScreenHero
    >
      <NewProductIntro
        title={t<string>("new-product-intro.title")}
        description={t<string>("new-product-intro.description")}
      />
      {locale === "za" ? (
        <CallOutAd
          title="What are employee benefits?"
          subtitle={
            <P1>
              Employee benefits, a form of group insurance cover, is offered by employers to
              safeguard their employees' financial health. It’s becoming a highly sought-after
              employee benefit, with comprehensive plans that can encompass{" "}
              <Anchor
                href={StaticRoutes.insuranceGroupLifeInsurance}
                color={color.P100}
                $isUnderlined
              >
                Life Cover
              </Anchor>
              ,{" "}
              <Anchor
                href={StaticRoutes.insuranceGroupIncomeProtection}
                color={color.P100}
                $isUnderlined
              >
                Income Protection
              </Anchor>
              ,{" "}
              <Anchor
                href={StaticRoutes.insuranceLumpSumDisability}
                color={color.P100}
                $isUnderlined
              >
                Lump Sum Disability
              </Anchor>{" "}
              and{" "}
              <Anchor href={StaticRoutes.insuranceFuneralCover} color={color.P100} $isUnderlined>
                {" "}
                Funeral Cover
              </Anchor>
              , among other benefits.
            </P1>
          }
          backgroundColor={color.S210}
          textColor={color.B1100}
          margin="0 auto"
          padding="0rem 2rem 8rem"
          wrapBackgroundColour={color.B210}
        >
          <YugiCallOutImg
            width={232}
            height={360}
            marginRight="6rem"
            src="/static/pages/benefits/Yugi-hugging-question-mark.svg"
            alt="Yugi the giraffe wrapping neck around a large purple question mark on blue background."
          />
        </CallOutAd>
      ) : null}
      <NewProductIntro title="How it works" backgroundColor={color.white} pt="7.5rem" mobPb="0" />
      <NewZigZag
        title={t<string>("zig-zag.first-section.title")}
        description={t<string>("zig-zag.first-section.description")}
        linkText="Explore our products"
        linkUrl={StaticRoutes.insuranceGroupInsurance}
        align="left"
        pt="0rem"
        pb="0rem"
      >
        <ResponsiveZigZagImage
          src={t<string>("zig-zag.first-section.img-src")}
          alt={t<string>("zig-zag.first-section.img-alt")}
        />
      </NewZigZag>
      <NewZigZag
        title={t<string>("zig-zag.second-section.title")}
        description={t<string>("zig-zag.second-section.description")}
        reverse
        align="left"
        pt="8rem"
        pb="0rem"
        linkText="Find out more"
        linkUrl={StaticRoutes.featuresEmployeeApp}
      >
        <ResponsiveZigZagImage
          src={t<string>("zig-zag.second-section.img-src")}
          alt={t<string>("zig-zag.second-section.img-alt")}
        />
      </NewZigZag>
      <NewZigZag
        title={t<string>("zig-zag.third-section.title")}
        description={
          <>
            {t.rich("zig-zag.third-section.description", {
              health: (chunks) => (
                <Anchor
                  key="zig-zag.third-section.description.health"
                  color={color.P100}
                  href={StaticRoutes.featuresEmployeeAssistanceProgramme}
                  $isUnderlined
                >
                  {chunks}
                </Anchor>
              ),
              gp: (chunks) => (
                <Anchor
                  key="zig-zag.third-section.description.gp"
                  color={color.P100}
                  href={StaticRoutes.featuresVirtualGpServices}
                  $isUnderlined
                >
                  {chunks}
                </Anchor>
              ),
            })}
          </>
        }
        align="left"
        pt="8rem"
        pb="4rem"
        linkText="Explore the Wellbeing Hub"
        linkUrl={StaticRoutes.featuresWellbeingHub}
      >
        <ResponsiveZigZagImage
          src={t<string>("zig-zag.third-section.img-src")}
          alt={t<string>("zig-zag.third-section.img-alt")}
        />
      </NewZigZag>
      <TabSwitcherProducts />
      {locale != "za" ? (
        <CallOutAd
          title={t<string>("call-out-ad.title")}
          subtitle={t<string>("call-out-ad.description")}
          buttonText={t<string>("call-out-ad.buttonText")}
          titleColor={t<string>("call-out-ad.titleColor")}
          backgroundColor={t<string>("call-out-ad.backgroundColor")}
          buttonLink={t<string>("call-out-ad.buttonLink")}
          textColor={t<string>("call-out-ad.textColor")}
        >
          <YugiCallOutImg
            height={locale === "us" ? 420 : 446}
            width={locale === "us" ? 402 : 325}
            marginRight={locale === "us" ? "0" : "2rem"}
            src={t<string>("call-out-ad.src")}
            alt={t<string>("call-out-ad.alt")}
          />
        </CallOutAd>
      ) : null}

      <QuoteCarousel
        slides={[
          {
            quote:
              "YuLife ticked all the boxes. It was the answer to our insurance needs. Wellness and engagement in benefits that employees were not using previously.",
            source: "Alix Hobbs, Director of Operations, Cloud International",
            avatar: "/static/img/carousel/alix-cloud.png",
            vimeoId: "769492152",
          },
          {
            quote:
              "YuLife has given us the tools to make the most of our existing insurance investment and go the extra mile to boost employee wellbeing.",
            source: "Ewen PacPherson, Group Chief People Officer, Havas Group",
            avatar: "/static/img/carousel/ewen-havas.png",
          },
        ]}
      />
      <BottomSection
        keyResources={keyResources || []}
        requestCallbackProps={{
          title: "Ready to hear how we can help?",
          subtitle:
            "Get a free quote and find out how our simple and affordable group insurance can help your employees live healthier and happier lives.",
          buttonText: "Speak to our team",
        }}
      >
        <StandardSection pt="2rem">
          <H2 align="center" pt="1rem" pb="3rem">
            Frequently asked questions.
          </H2>
          <>{faq}</>
        </StandardSection>
      </BottomSection>
    </Page>
  );
};

export default EmployeeBenefits;
